import { z } from 'zod';

const insightsSubviews = ['findings', 'detail'] as const;
export type InsightsSubview = (typeof insightsSubviews)[number];

/**
 * Which loop's run is being read, as the URL states it: absent means the loop navigated to, `'all'`
 * means every readable loop merged, and a number means that loop specifically. Never stored as a
 * bare `number | null` in the URL — an absent param has to mean "the nav loop", not "all loops", so
 * the two need distinct spellings. `loopScope.ts` turns this into the `number | null` the rest of the
 * page reasons about.
 */
export const loopParamSchema = z
  .union([z.literal('all'), z.coerce.number().int().positive()])
  .optional()
  .catch(undefined);

/**
 * The whole surface's state lives in the URL, so a link reproduces exactly what someone was looking
 * at — which run, which reading, filtered to which objective, with which finding open, and (NEX-18040)
 * which loop's run.
 *
 * Every field is `.catch(undefined)` rather than plain `.optional()`: these arrive from a
 * hand-editable address bar, and an unrecognised subview should fall back to the default reading
 * rather than throw a route error at someone who mistyped a link.
 */
export const experimentalSearchSchema = z.object({
  runId: z.string().min(1).optional().catch(undefined),
  view: z.enum(insightsSubviews).optional().catch(undefined),
  objective: z.string().min(1).optional().catch(undefined),
  finding: z.string().min(1).optional().catch(undefined),
  loop: loopParamSchema,
});

export type ExperimentalSearch = z.infer<typeof experimentalSearchSchema>;
